import { useCallback, useEffect, useState } from 'react';
import {
  actualizarPremio,
  borrarPremio,
  crearPremio,
  guardarConfig,
  listarCanjes,
  listarPremios,
  obtenerConfig,
  resolverCanje,
  type CanjeRow,
  type ConfigEspacio,
  type PremioRow,
} from '../../api/academia';

/**
 * Configuración del espacio y catálogo de premios, más la bandeja de canjes.
 *
 * Van juntos a propósito: un premio que nadie puede aprobar es una promesa rota, así que
 * el catálogo y la bandeja que lo resuelve tienen que verse en la misma pantalla.
 */
export default function AcademiaConfig({
  espacioId,
  editable,
}: {
  espacioId: string;
  editable: boolean;
}) {
  const [config, setConfig] = useState<ConfigEspacio | null>(null);
  const [premios, setPremios] = useState<PremioRow[]>([]);
  const [canjes, setCanjes] = useState<CanjeRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [puntosNuevo, setPuntosNuevo] = useState('100');

  const recargar = useCallback(async () => {
    if (!espacioId) return;
    try {
      const [c, p, k] = await Promise.all([
        obtenerConfig(espacioId),
        listarPremios(espacioId),
        listarCanjes(),
      ]);
      setConfig(c);
      setPremios(p);
      setCanjes(k);
    } catch (err) {
      setError(mensaje(err));
    }
  }, [espacioId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  async function accion(fn: () => Promise<void>, textoAviso = 'Guardado') {
    setError(null);
    try {
      await fn();
      await recargar();
      setAviso(textoAviso);
      setTimeout(() => setAviso(''), 2000);
    } catch (err) {
      setError(mensaje(err));
    }
  }

  if (!config) return <p className="py-8 text-center text-gray-500">Cargando…</p>;

  const pendientes = canjes.filter((c) => c.estado === 'solicitado');

  return (
    <div className="space-y-6">
      {error && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      {aviso && (
        <p className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-800">{aviso}</p>
      )}

      {/* ---------- reglas del espacio ---------- */}
      <section className="rounded-lg border bg-white p-4">
        <h2 className="mb-1 font-bold">Reglas de aprobación</h2>
        <p className="mb-3 text-sm text-gray-500">
          Valen para todo el espacio. Un curso puede tener su propia nota mínima, y esa manda
          sobre esta.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <NumeroConfig
            etiqueta="Nota mínima para aprobar"
            ayuda="De 0 a 100. La demo usa 60."
            valor={config.notaMinima}
            editable={editable}
            min={0}
            max={100}
            onGuardar={(n) => accion(() => guardarConfig(espacioId, 'nota_minima', n))}
          />
          <NumeroConfig
            etiqueta="Bonus por nota perfecta"
            ayuda="Puntos extra al sacar 100 la primera vez que se aprueba."
            valor={config.bonusNotaPerfecta}
            editable={editable}
            min={0}
            max={500}
            onGuardar={(n) => accion(() => guardarConfig(espacioId, 'bonus_nota_perfecta', n))}
          />
        </div>
      </section>

      {/* ---------- premios ---------- */}
      <section className="rounded-lg border bg-white p-4">
        <h2 className="mb-1 font-bold">Catálogo de premios</h2>
        <p className="mb-3 text-sm text-gray-500">
          Lo que el recurso ve en su pestaña de Recompensas. Desactivar un premio lo saca del
          catálogo sin borrar los canjes que ya se pidieron.
        </p>

        {editable && (
          <div className="mb-3 flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
            <input
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              placeholder="Nombre del premio"
              className="min-w-[200px] flex-1 rounded-lg border px-3 py-2 text-sm"
            />
            <input
              value={puntosNuevo}
              onChange={(e) => setPuntosNuevo(e.target.value)}
              inputMode="numeric"
              className="w-28 rounded-lg border px-3 py-2 text-sm"
              placeholder="Puntos"
              aria-label="Puntos del premio nuevo"
            />
            <button
              onClick={() =>
                accion(async () => {
                  const n = Number(puntosNuevo);
                  if (!nombreNuevo.trim()) throw new Error('Ponle un nombre al premio.');
                  if (!Number.isInteger(n) || n <= 0) {
                    throw new Error('Los puntos deben ser un número mayor que cero.');
                  }
                  await crearPremio(espacioId, nombreNuevo.trim(), n, premios.length + 1);
                  setNombreNuevo('');
                }, 'Premio creado')
              }
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
            >
              + Premio
            </button>
          </div>
        )}

        <div className="space-y-2">
          {premios.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              Todavía no hay premios en el catálogo.
            </p>
          ) : (
            premios.map((p) => (
              <div
                key={p.id}
                className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${
                  p.activo ? '' : 'opacity-50'
                }`}
              >
                <TextoEnLinea
                  valor={p.icono}
                  editable={editable}
                  ancho="w-14 text-center text-xl"
                  onGuardar={(v) => accion(() => actualizarPremio(p.id, { icono: v }))}
                />
                <div className="min-w-[180px] flex-1 space-y-1">
                  <TextoEnLinea
                    valor={p.nombre}
                    editable={editable}
                    onGuardar={(v) => accion(() => actualizarPremio(p.id, { nombre: v }))}
                  />
                  <TextoEnLinea
                    valor={p.detalle}
                    editable={editable}
                    marcador="Detalle (dónde se entrega, condiciones…)"
                    onGuardar={(v) => accion(() => actualizarPremio(p.id, { detalle: v }))}
                  />
                </div>
                <TextoEnLinea
                  valor={String(p.puntos)}
                  editable={editable}
                  ancho="w-24"
                  onGuardar={(v) =>
                    accion(async () => {
                      const n = Number(v);
                      if (!Number.isInteger(n) || n <= 0) {
                        throw new Error('Los puntos deben ser un número mayor que cero.');
                      }
                      await actualizarPremio(p.id, { puntos: n });
                    })
                  }
                />
                {editable && (
                  <>
                    <label className="flex items-center gap-1.5 text-xs font-semibold">
                      <input
                        type="checkbox"
                        checked={p.activo}
                        onChange={(e) =>
                          accion(() => actualizarPremio(p.id, { activo: e.target.checked }))
                        }
                      />
                      Activo
                    </label>
                    <button
                      onClick={() =>
                        accion(async () => {
                          if (!confirm(`¿Borrar «${p.nombre}» del catálogo?`)) return;
                          await borrarPremio(p.id);
                        }, 'Premio borrado')
                      }
                      className="text-xs text-red-700"
                      title="Si ya tiene canjes, no se puede borrar: desactívalo"
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* ---------- canjes ---------- */}
      <section className="rounded-lg border bg-white p-4">
        <h2 className="mb-1 font-bold">
          Canjes por resolver
          {pendientes.length > 0 && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
              {pendientes.length}
            </span>
          )}
        </h2>
        <p className="mb-3 text-sm text-gray-500">
          Los puntos se descuentan <b>al aprobar</b>. Un canje rechazado no le cuesta nada al
          recurso.
        </p>

        {canjes.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            Nadie ha solicitado un canje todavía.
          </p>
        ) : (
          <div className="space-y-2">
            {canjes.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                <span className="text-xl">{c.premio_icono}</span>
                <div className="min-w-[200px] flex-1">
                  <p className="text-sm font-semibold">{c.premio_nombre}</p>
                  <p className="text-xs text-gray-500">
                    {c.worker_nombre} · {c.worker_dni} · {c.puntos_gastados} pts ·{' '}
                    {new Date(c.solicitado_at).toLocaleDateString('es-PE', {
                      timeZone: 'America/Lima',
                    })}
                  </p>
                </div>

                {c.estado === 'solicitado' ? (
                  editable ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => accion(() => resolverCanje(c.id, 'aprobado'), 'Canje aprobado')}
                        className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() =>
                          accion(async () => {
                            const motivo = prompt('Motivo del rechazo (opcional):') ?? undefined;
                            await resolverCanje(c.id, 'rechazado', motivo);
                          }, 'Canje rechazado')
                        }
                        className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                      >
                        Rechazar
                      </button>
                    </div>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">
                      Pendiente
                    </span>
                  )
                ) : (
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      c.estado === 'aprobado'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                    title={c.nota ?? undefined}
                  >
                    {c.estado === 'aprobado' ? 'Aprobado' : 'Rechazado'}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function NumeroConfig({
  etiqueta,
  ayuda,
  valor,
  editable,
  min,
  max,
  onGuardar,
}: {
  etiqueta: string;
  ayuda: string;
  valor: number;
  editable: boolean;
  min: number;
  max: number;
  onGuardar: (n: number) => Promise<void> | void;
}) {
  const [borrador, setBorrador] = useState(String(valor));
  useEffect(() => setBorrador(String(valor)), [valor]);

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold">{etiqueta}</span>
      <input
        value={borrador}
        disabled={!editable}
        inputMode="numeric"
        onChange={(e) => setBorrador(e.target.value)}
        onBlur={() => {
          const n = Number(borrador);
          // Se valida acá y no solo en la base: un 150 rebotado por el CHECK sale con un
          // error de Postgres que no le dice nada a nadie.
          if (!Number.isInteger(n) || n < min || n > max) {
            setBorrador(String(valor));
            return;
          }
          if (n !== valor) void onGuardar(n);
        }}
        className="w-full rounded-lg border px-3 py-2 text-sm disabled:bg-gray-50"
      />
      <span className="mt-1 block text-xs text-gray-500">{ayuda}</span>
    </label>
  );
}

function TextoEnLinea({
  valor,
  editable,
  ancho = 'w-full',
  marcador,
  onGuardar,
}: {
  valor: string;
  editable: boolean;
  ancho?: string;
  marcador?: string;
  onGuardar: (v: string) => Promise<void> | void;
}) {
  const [borrador, setBorrador] = useState(valor);
  useEffect(() => setBorrador(valor), [valor]);

  return (
    <input
      value={borrador}
      disabled={!editable}
      placeholder={marcador}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={() => {
        if (borrador !== valor) void onGuardar(borrador);
      }}
      className={`${ancho} rounded-lg border px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-500`}
    />
  );
}

function mensaje(err: unknown): string {
  const m = err instanceof Error ? err.message : String(err);
  if (m.includes('PGRST106')) {
    return 'El esquema `academia` no está expuesto en la API del proyecto (Settings → API → Exposed schemas).';
  }
  if (m.includes('42501')) return 'No tienes permiso para esta acción.';
  // `canjes.premio_id` es on delete restrict: el error crudo no explica el porqué.
  if (m.includes('violates foreign key') || m.includes('23503')) {
    return 'Ese premio ya tiene canjes solicitados, así que no se puede borrar. Desactívalo en su lugar.';
  }
  return m;
}
